import classNames from 'classnames';
import React, { useCallback } from 'react';

import Link from 'next/link';

import { PropsWithClassname } from '@/app/types';
import { CategoryDetail } from '@/app/api';

import Button, { ButtonHTMLTag, ButtonStyle } from '@/app/components/Button/Button';

interface CategoriesBoxProps extends PropsWithClassname {
  heading: string;
  text: string;
  categories: CategoryDetail[];
  buttonText: string;
  buttonLink: string;
}

const CategoriesBox = ({ heading, text, categories, buttonText, buttonLink, className }: CategoriesBoxProps) => {
  const getCategoryLink = useCallback((slug?: string) => {
    if (!slug) {
      return '#';
    }

    const baseUrl = process.env.NEXT_PUBLIC_REDIRECT_URL;

    if (baseUrl?.endsWith('/')) {
      return `${baseUrl}${slug}`;
    }

    return `${baseUrl}/${slug}`;
  }, []);

  return (
    <div
      className={classNames(className, 'bg-white border border-neutral-light rounded-md shadow-md px-[24px] py-[32px]')}
    >
      <h2 className="text-[20px] leading-[24px] lg:text-[24px] lg:leading-[32px] mb-[16px] mb-[24px]">{heading}</h2>
      <p className="text-[16px] leading-[24px] mb-[16px]">{text}</p>
      <div className="flex flex-wrap mb-[32px]">
        {categories.map((category) => (
          <Link
            key={category.id}
            href={getCategoryLink(category.slug)}
            prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
            className="block bg-secondary-light border-[2px] border-secondary-light hover:border-primary-mid transition-all px-[16px] py-[2px] rounded-full pointer uppercase text-nowrap text-[12px] leading-[24px] tracking-[2px] font-medium mr-[8px] mb-[8px]"
          >
            {category.name}
          </Link>
        ))}
      </div>
      <Button tagSpecificProps={{ htmlTag: ButtonHTMLTag.LINK, href: buttonLink }} style={ButtonStyle.PRIMARY_OUTLINED}>
        {buttonText}
      </Button>
    </div>
  );
};

export default CategoriesBox;
